import { GoogleMap, InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../../../assets/icons/alert-icon.svg';
import AlertStationIcon from '../../../../assets/icons/dashboard/alert-station-locator.svg';
import { ReactComponent as RightArrow } from '../../../../assets/icons/dashboard/right-arrow.svg';
import { ReactComponent as UserIcon } from '../../../../assets/icons/dashboard/user-icon.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';
import { brandIcons, brandImage, metaValues } from '../../../../config/branding';
import { useGoogleMaps } from '../../../../contexts/google-map-context/GoogleMapContent';
import { StationDetails } from '../../../../interface/dashboard-partner.interface';
import AutoCompleteSelectField from '../../../../shared-components/auto-complete-select-field/AutoCompleteSelectField';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import dashboard from '../../../../utils/api/partner/dashboard';

interface StationProperties {
  id: string;
  position: { lat: number; lng: number };
  alert: boolean;
  name: string;
  address: string;
  isChargingPointAvailable?: boolean;
  isFaulted?: boolean;
  connectorStatuses?: any[];
}

/**
 * Validates the position of a station.
 * @param {any} position - The position object.
 * @param {any} position.lat - The latitude value.
 * @param {any} position.lng - The longitude value.
 * @returns {any} The validated position or null if invalid.
 */
const validatePosition = (position: { lat: any; lng: any }) => {
  const lat = parseFloat(position.lat);
  const lng = parseFloat(position.lng);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">

      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />

      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Resolves the color of the station marker based on connector status.
 * Priority: Available → Charging → Reserved → Red
 * @param {StationProperties} marker - The station properties.
 * @returns {string} The hex color code.
 */
const getStationColor = (marker: StationProperties): string => {
  const statuses = marker.connectorStatuses || [];

  // 🟢 GREEN
  // If ANY connector is Available, show green.
  // Example: 20 connectors and even 1 is Available => Green.
  const hasAvailable = statuses.some((item) => item.status === 'Available');

  if (hasAvailable) {
    return '#22c55e';
  }

  // 🔵 BLUE
  // No Available connector.
  // If ANY connector is Charging / Preparing / Finishing => Blue.
  const hasCharging = statuses.some(
    (item) =>
      item.status === 'Charging' || item.status === 'Preparing' || item.status === 'Finishing',
  );

  if (hasCharging) {
    return '#3b82f6';
  }

  // 🟡 YELLOW
  // No Available and no Charging status.
  // If ANY connector is Reserved => Yellow.
  const hasReserved = statuses.some((item) => item.status === 'Reserved');

  if (hasReserved) {
    return '#FFC107';
  }

  // 🔴 RED
  // No Available, Charging, Preparing, Finishing, or Reserved.
  return '#e41010';
};

/**
 * Map component to station location
 * @returns {any} The result
 */
function DashboardMap() {
  const { t } = useTranslation('dashboard');
  const { isLoaded } = useGoogleMaps();
  const navigate = useNavigate();
  const [showCards, setShowCards] = useState<boolean>(true);
  const [searchStation, setSearchStation] = useState<string | number>('');
  const [singleStationDetails, setSingleStationDetails] = useState<StationDetails>({
    _id: '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    opensAt: '', // ISO 8601 string format
    closesAt: '', // ISO 8601 string format
    isAvailable: false,
    connectors: [], // Array of connector types
    totalChargingPoints: 0,
    clientsOnStation: 0,
  });
  const [selectedMarker, setSelectedMarker] = useState<StationProperties | null>(null);

  const [stationLocation, setStationLocation] = useState<StationProperties[]>([]);

  const [center, setCenter] = useState({
    lat: metaValues.latLong.lat,
    lng: metaValues.latLong.lng,
  });

  /**
   * Helper to retrieve daily operating hours formatting for today.
   * @returns {string} The formatted operating hours label.
   */
  const getTodayHours = (): string => {
    if (!singleStationDetails) return '';

    let parsedHours: any = null;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof singleStationDetails.operatingHours === 'string') {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        parsedHours = JSON.parse(singleStationDetails.operatingHours);
      } catch {
        parsedHours = null;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } else if (singleStationDetails.operatingHours) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      parsedHours = singleStationDetails.operatingHours;
    }

    if (parsedHours) {
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ] as const;
      const today = dayNames[new Date().getDay()];
      const dayData = parsedHours[today];
      if (dayData) {
        if (dayData.status === 'closed') {
          return t('closedToday', 'Closed Today');
        }
        const openTime = dayData.open || dayData.from;
        const closeTime = dayData.close || dayData.to;
        if (openTime === '00:00' && closeTime === '23:59') {
          return t('open24Hours', 'Open 24 Hours');
        }
        if (openTime && closeTime) {
          return `${openTime} - ${closeTime}`;
        }
      }
    }

    // Fallback to opensAt and closesAt
    if (singleStationDetails.opensAt && singleStationDetails.closesAt) {
      return `${ConvertUTCtoHHMM(singleStationDetails.opensAt)} - ${ConvertUTCtoHHMM(
        singleStationDetails.closesAt,
      )}`;
    }

    return '--';
  };

  /**
   * Function to fetch single station details
   * @param {any} marker - The marker
   */
  const handleFetchSingleStation = async (marker: StationProperties) => {
    const validPosition = validatePosition(marker.position);
    if (validPosition) {
      setCenter(validPosition);
      setSelectedMarker({ ...marker, position: validPosition });
      setShowCards(false);
      try {
        const response = await dashboard.getStationDetails(marker.id);
        if (response.data.length) {
          setSingleStationDetails(response.data?.[0]);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  /**
   * Function to filter the station list by name or address
   * @param {string} name - The name
   * @param {string | number} value - The value
   */
  const handleFilterByStationDetails = (name: string, value: string | number) => {
    setSearchStation(value);

    const matchedStation: StationProperties | undefined = stationLocation.find(
      (station: StationProperties) => station.name === value,
    );

    if (matchedStation) {
      handleFetchSingleStation(matchedStation);
    }
  };

  /**
   * Function to fetch station latitude and longitude
   */
  const fetchStationLatLong = async () => {
    const response = await dashboard.getStationMap();
    const mapLocations = response?.data || [];
    if (mapLocations?.length > 0) {
      const availableStations = mapLocations.map((location: any) => ({
        id: location._id,
        position: { lat: parseFloat(location?.latitude), lng: parseFloat(location?.longitude) },
        alert: location?.isFault,
        name: location?.name,
        address: location?.address,
        isChargingPointAvailable: location?.isChargingPointAvailable,
        isFaulted: location?.isFaulted,
        connectorStatuses: location?.connectorStatuses,
      }));

      setStationLocation(availableStations);

      const firstStation = availableStations[0];
      const validFirstPos = validatePosition(firstStation.position);
      setCenter(
        validFirstPos || {
          lat: metaValues.latLong.lat,
          lng: metaValues.latLong.lng,
        },
      );
    }
  };

  useEffect(() => {
    /**
     * Function to map details one by one
     */
    const getCardsCount = async () => {
      try {
        await fetchStationLatLong();
      } catch (error) {
        console.error(error);
      }
    };

    getCardsCount();
  }, []);

  const containerStyle = {
    width: '100%',
    height: '100%',
  };

  return (
    <div className="dashboard-map-wrap-partner" onMouseLeave={() => setShowCards(true)}>
      {/* <h1>Overview</h1> */}
      <div className="dashboard-map-container">
        {/* Google map container */}
        {isLoaded ? (
          <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={7}>
            {/* Marker Clusterer for grouping nearby markers */}
            <MarkerClusterer>
              {(clusterer) => (
                <>
                  {stationLocation.map((marker) => {
                    const validPos = validatePosition(marker.position);
                    return (
                      validPos && (
                        <Marker
                          key={marker.id}
                          position={validPos}
                          clusterer={clusterer}
                          icon={
                            marker.alert
                              ? {
                                  url: AlertStationIcon,
                                  scaledSize: new window.google.maps.Size(76, 47), // Width, Height
                                }
                              : {
                                  url: getStationMarkerIcon(getStationColor(marker)),
                                  scaledSize: new window.google.maps.Size(44, 45), // Width, Height
                                }
                          }
                          onClick={() => handleFetchSingleStation(marker)} // Open popup on marker click
                        />
                      )
                    );
                  })}
                </>
              )}
            </MarkerClusterer>
            {/* Popup for marker */}
            {selectedMarker && (
              <InfoWindow
                position={selectedMarker.position}
                onCloseClick={() => setSelectedMarker(null)}>
                {selectedMarker.alert ? (
                  <div className="map-station-details-wrap alert-wrap">
                    <div className="map-station-details-container">
                      <span>
                        <AlertIcon />
                      </span>
                      <p>{t('stationPerformanceAlerts')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="map-station-active-container">
                    <div className="map-station-details">
                      <div className="details-header">
                        <div className="details-header-image-wrap">
                          <img
                            src={brandImage.chargingDevice}
                            alt=""
                            className="details-header-image"
                          />
                        </div>
                        <div className="name-and-status">
                          <h2>{singleStationDetails.name}</h2>
                          {(() => {
                            let isActive = singleStationDetails.isAvailable;
                            let statusText = isActive ? t('active') : t('inactive');

                            let parsedHours: any = null;
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            if (typeof singleStationDetails.operatingHours === 'string') {
                              try {
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                parsedHours = JSON.parse(singleStationDetails.operatingHours);
                              } catch {
                                parsedHours = null;
                              }
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            } else if (singleStationDetails.operatingHours) {
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              parsedHours = singleStationDetails.operatingHours;
                            }

                            if (parsedHours) {
                              const dayNames = [
                                'sunday',
                                'monday',
                                'tuesday',
                                'wednesday',
                                'thursday',
                                'friday',
                                'saturday',
                              ] as const;
                              const today = dayNames[new Date().getDay()];
                              const dayData = parsedHours[today];
                              if (dayData && dayData.status) {
                                if (dayData.status.toLowerCase() === 'open') {
                                  isActive = true;
                                  statusText = t('open', 'Open');
                                } else if (dayData.status.toLowerCase() === 'closed') {
                                  isActive = false;
                                  statusText = t('closed', 'Closed');
                                }
                              }
                            }

                            return (
                              <div className={`status-wrap ${isActive ? 'active' : 'in-active'}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <div className={`status-point ${isActive ? '' : 'in-active'}`} />
                                  <p>{statusText}</p>
                                </div>
                                {!singleStationDetails.isAvailable && (
                                  <p style={{ color: '#ff4d4f', fontSize: '12px', margin: 0 }}>
                                    {t('publiclyNotVisible', '(Publicly not visible)')}
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="address-details">
                        <div className="place-with-icon">
                          <span />
                          {/* To-do - need to show country here or need to confirm */}
                          <span>
                            <LocationIcon className="location-icon" />
                          </span>
                        </div>
                        <p>{singleStationDetails.address}</p>
                      </div>
                      <div className="user-details">
                        <span>
                          <UserIcon />
                        </span>
                        <span className="count">{singleStationDetails.clientsOnStation}</span>
                        <span className="message">{t('clientOnStation')}</span>
                      </div>
                    </div>
                    <div className="map-station-center-border" />
                    <div className="map-station-availability-wrap">
                      <div className="availability-status-wrap">
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardClock />
                          </span>
                          <p>{getTodayHours()}</p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardGas />
                          </span>
                          <p>
                            {singleStationDetails.totalChargingPoints} {t('devices')}
                          </p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardCharge />
                          </span>
                          <p>
                            {singleStationDetails?.connectors?.length > 0
                              ? singleStationDetails.connectors.join(', ')
                              : '0'}{' '}
                            {t('connectors')}
                          </p>
                        </div>
                      </div>
                      <div className="view-more-button">
                        <MyButton
                          label={t('viewMore')}
                          buttonType="submit"
                          disableHoverEffect
                          svgIcon={<RightArrow />}
                          onClickFunc={() => navigate('../station-management')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </InfoWindow>
            )}
          </GoogleMap>
        ) : (
          <div className="dashboard-map-loader">
            <BatteryLoader />
          </div>
        )}
        {/* Absolute search box */}
        <div className="map-search-wrap" role="presentation" onClick={() => setShowCards(false)}>
          <AutoCompleteSelectField
            inputLabel=""
            placeholder={t('search')}
            name="search"
            selectedValue={searchStation}
            onChangeValue={handleFilterByStationDetails}
            svgIcon={<SearchIcon />}
            options={stationLocation.map((station) => ({
              label: `${station.name} (${station.address})`,
              value: station.name,
            }))}
          />
        </div>
        {/* Absolute background color */}
        {showCards && <div className="absolute-background" />}
      </div>
    </div>
  );
}

export default DashboardMap;
